/**
 * DGArtifactsSourceDashboard.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Source-level governance dashboard for the DG tab.
 *
 *  • Fetches GET /adriva_api/dg/source-summary on mount + when src/app changes
 *  • Displays aggregate KPIs (totals, sensitivity, CDE, risk, regulatory, etc)
 *  • Lists entities with checkboxes so the SME can choose which entities to
 *    deep-review in GovernanceUI
 *  • When "Open DG Review" is clicked it bubbles the selected entityIds up
 *    via onOpenReview — the parent then triggers GovernanceUI in lazy/batch
 *    mode
 *
 * Visual language follows the same token system used in GovernanceUI.tsx, so
 * the two components feel like one cohesive product.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  adriva_dg_getSourceSummary,
  adriva_exportGovernanceExcel,
  DGSourceSummaryResponse,
  DGEntitySummary,
} from "../API";

// ─── DESIGN TOKENS (mirror GovernanceUI.tsx) ──────────────────────────────────
const T = {
  bg:          "#eef1f6",
  bgCard:      "#ffffff",
  bgSidebar:   "#f5f7fa",
  textPrimary: "#0f1923",
  textSecond:  "#1e2d3d",
  textMuted:   "#445566",
  textLabel:   "#2c3e52",
  brand:       "#1e3a6e",
  brandLight:  "#dde7f5",
  brandMid:    "#90aed0",
  border:      "#c8d4e0",
  green:       "#166534",
  greenBg:     "#f0fdf4",
  greenBorder: "#86efac",
  amber:       "#7c2d00",
  amberBg:     "#fff7ed",
  amberBorder: "#fde68a",
  red:         "#991b1b",
  redBg:       "#fef2f2",
  redBorder:   "#fca5a5",
};

const SENS_COLORS: Record<string, { color: string; bg: string; border: string }> = {
  "Restricted":            { color: "#991b1b", bg: "#fef2f2", border: "#fca5a5" },
  "Highly Confidential":   { color: "#9a3412", bg: "#fff7ed", border: "#fdba74" },
  "Confidential":          { color: "#713f12", bg: "#fefce8", border: "#fde047" },
  "Business Confidential": { color: "#713f12", bg: "#fefce8", border: "#fde047" },
  "Unrestricted":          { color: "#166534", bg: "#f0fdf4", border: "#86efac" },
  "Unknown":               { color: "#445566", bg: "#f5f7fa", border: "#c8d4e0" },
};

const CDE_COLORS: Record<string, { color: string; bg: string; border: string }> = {
  "Critical":    { color: "#991b1b", bg: "#fef2f2", border: "#fca5a5" },
  "Significant": { color: "#9a3412", bg: "#fff7ed", border: "#fdba74" },
  "Important":   { color: "#713f12", bg: "#fefce8", border: "#fde047" },
  "Standard":    { color: "#166534", bg: "#f0fdf4", border: "#86efac" },
  "Unknown":     { color: "#445566", bg: "#f5f7fa", border: "#c8d4e0" },
};

// ─── PROPS ────────────────────────────────────────────────────────────────────
export interface SourceDashboardProps {
  appNm:          string;
  srcSystemId:    string;
  srcSystemName?: string;
  /** Setup entries for THIS source (from app.setup filtered by srcSystemId).
   *  Used as the master list for checkbox rendering even when summary is
   *  still loading. */
  entitiesInSource: Array<{
    entityId: string;
    entity:   string;
    domain?:  string;
  }>;
  /** Optional: entities already selected (e.g. when navigating back) */
  initialSelectedIds?: string[];
  /** Bubble selection + summary up to parent, which then loads GovernanceUI
   *  in batch mode. */
  onOpenReview: (selectedEntityIds: string[]) => void;
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function SourceDashboard({
  appNm,
  srcSystemId,
  srcSystemName,
  entitiesInSource,
  initialSelectedIds,
  onOpenReview,
}: SourceDashboardProps) {

  const [summary, setSummary] = useState<DGSourceSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Default selection: everything in the source
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() =>
    new Set(initialSelectedIds && initialSelectedIds.length > 0
      ? initialSelectedIds
      : entitiesInSource.map(e => e.entityId)),
  );

  // ── Fetch source summary ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    adriva_dg_getSourceSummary(appNm, srcSystemId)
      .then(res => {
        if (cancelled) return;
        if (res.success) setSummary(res);
        else             setErr("Failed to load summary");
      })
      .catch(e => {
        if (cancelled) return;
        setErr(e?.detail || e?.message || "Network error");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [appNm, srcSystemId]);

  // ── Sync selection when entity list changes ─────────────────────────────────
  useEffect(() => {
    setSelectedIds(prev => {
      // Keep selections valid; if list grew, auto-include new ones for first render
      const validIds = new Set(entitiesInSource.map(e => e.entityId));
      const next = new Set<string>();
      prev.forEach(id => { if (validIds.has(id)) next.add(id); });
      // If user had nothing selected (edge case after a switch), seed all
      if (next.size === 0) {
        entitiesInSource.forEach(e => next.add(e.entityId));
      }
      return next;
    });
  }, [entitiesInSource]);

  // ── Filtered aggregate based on current checkbox selection ──────────────────
  const filteredAgg = useMemo(() => {
    if (!summary) return null;
    const picked = summary.entities.filter(e => selectedIds.has(e.entity_id));
    return reduceEntities(picked);
  }, [summary, selectedIds]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const toggleEntity = useCallback((eid: string) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.has(eid) ? n.delete(eid) : n.add(eid);
      return n;
    });
  }, []);
  const selectAll  = () => setSelectedIds(new Set(entitiesInSource.map(e => e.entityId)));
  const selectNone = () => setSelectedIds(new Set());
  const handleExport = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setExporting(true);
    try {
      await adriva_exportGovernanceExcel(appNm, "", [], [...selectedIds]);
    } catch (e) {
      console.error("Excel export failed:", e);
    } finally {
      setExporting(false);
    }
  }, [appNm, selectedIds]);

  // ──────────────────────────────────────────────────────────────────────────
  if (loading) return <LoadingShell />;
  if (err)     return <ErrorShell msg={err} />;
  if (!summary) return null;

  const totalEntities    = entitiesInSource.length;
  const selectedCount    = selectedIds.size;
  const allSelected      = selectedCount === totalEntities && totalEntities > 0;
  const summaryByEntity  = new Map(summary.entities.map(e => [e.entity_id, e]));

  return (
    <div style={{ padding: "20px 24px 28px", background: T.bg, minHeight: "100%" }}>

      {/* ── Header ── */}
      <div style={{
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        gap: 16, marginBottom: 16, flexWrap: "wrap",
      }}>
        <div>
          <div style={{
            fontSize: "0.64rem", fontWeight: 700, letterSpacing: "0.1em",
            textTransform: "uppercase", color: T.brand, marginBottom: 4,
          }}>
            Source Governance Overview
          </div>
          <div style={{
            fontSize: "1.18rem", fontWeight: 700, color: T.textPrimary,
            fontFamily: "'Lora',Georgia,serif", letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}>
            {srcSystemName || srcSystemId}
          </div>
          <div style={{ fontSize: "0.74rem", color: T.textMuted, marginTop: 4 }}>
            {totalEntities} entit{totalEntities === 1 ? "y" : "ies"} ·
            <span style={{ marginLeft: 4, color: T.brand, fontWeight: 600 }}>
              {selectedCount} selected for review
            </span>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={selectAll}  style={pillBtn(allSelected ? T.textMuted : T.brand, false)} disabled={allSelected}>
            Select all
          </button>
          <button onClick={selectNone} style={pillBtn(T.textMuted, true)} disabled={selectedCount === 0}>
            Clear
          </button>
          <button
            onClick={handleExport}
            disabled={selectedCount === 0 || exporting}
            style={{
              padding: "8px 16px", borderRadius: 8, border: `1px solid ${T.border}`,
              background: exporting ? T.bg : "#fff",
              color: selectedCount === 0 || exporting ? T.textMuted : T.brand,
              fontSize: "0.78rem", fontWeight: 600,
              cursor: selectedCount === 0 || exporting ? "default" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
              transition: "all 0.15s",
            }}
          >
            {exporting
              ? <><span style={{ display: "inline-block", animation: "dg-spin .8s linear infinite" }}>⟳</span> Exporting…</>
              : <>↓ Export Excel</>
            }
          </button>
          <style>{`@keyframes dg-spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
          <button
            onClick={() => onOpenReview([...selectedIds])}
            disabled={selectedCount === 0}
            style={{
              padding: "8px 18px", borderRadius: 8, border: "none",
              background: selectedCount === 0 ? T.border : T.brand,
              color: "#fff", fontSize: "0.78rem", fontWeight: 700,
              cursor: selectedCount === 0 ? "default" : "pointer",
              boxShadow: selectedCount === 0 ? "none" : "0 2px 8px rgba(30,58,110,0.20)",
              display: "inline-flex", alignItems: "center", gap: 6,
              transition: "all 0.15s",
            }}
          >
            Open DG Review →
          </button>
        </div>
      </div>

      {/* ── KPI strip ── */}
      
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(8,1fr)", gap: 8, marginBottom: 14,
      }}>
        <Kpi label="DM Fields"
            value={filteredAgg?.dm_field_count ?? 0}
            sub="in data model"
            color={T.brand} bg={T.brandLight} border={T.brandMid}/>
        <Kpi label="BM Enriched"
            value={filteredAgg?.bm_enriched ?? 0}
            sub={filteredAgg?.dm_field_count
              ? `${Math.round(((filteredAgg?.bm_enriched ?? 0) / filteredAgg.dm_field_count) * 100)}% coverage`
              : undefined}
            color={T.brand} bg={T.brandLight} border={T.brandMid}/>
        <Kpi label="MC Enriched"
            value={filteredAgg?.mc_enriched ?? 0}
            sub={filteredAgg?.dm_field_count
              ? `${Math.round(((filteredAgg?.mc_enriched ?? 0) / filteredAgg.dm_field_count) * 100)}% coverage`
              : undefined}
            color={T.brand} bg={T.brandLight} border={T.brandMid}/>
        <Kpi label="CDE Enriched"
            value={filteredAgg?.cde_enriched ?? 0}
            sub={filteredAgg?.dm_field_count
              ? `${Math.round(((filteredAgg?.cde_enriched ?? 0) / filteredAgg.dm_field_count) * 100)}% coverage`
              : undefined}
            color={filteredAgg?.cde_enriched ? "#9a3412" : T.textMuted}
            bg={filteredAgg?.cde_enriched ? "#fff7ed" : T.bg}
            border={filteredAgg?.cde_enriched ? "#fdba74" : T.border}/>
        <Kpi label="Approved"
            value={filteredAgg?.approved_count ?? 0}
            sub={`${filteredAgg?.approval_pct ?? 0}% reviewed`}
            color={T.green} bg={T.greenBg} border={T.greenBorder}/>
        <Kpi label="CDE Fields"
            value={filteredAgg?.cde_count ?? 0}
            sub={filteredAgg?.critical_cde ? `${filteredAgg.critical_cde} critical` : undefined}
            color="#991b1b" bg="#fef2f2" border="#fca5a5"/>
        <Kpi label="Personal Data"
            value={filteredAgg?.personal_data ?? 0}
            color={T.brand} bg={T.brandLight} border={T.brandMid}/>
        <Kpi label="Anon. Required"
            value={filteredAgg?.anon_required ?? 0}
            color="#713f12" bg="#fefce8" border="#fde047"/>
      </div>

      {/* ── Distribution row ── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)",
        gap: 12, marginBottom: 14,
      }}>
        <DistroCard title="Sensitivity Distribution"
                    data={filteredAgg?.sensitivity ?? {}}
                    palette={SENS_COLORS}
                    fallbackColor={T.brand}
                    order={["Restricted","Highly Confidential","Confidential","Business Confidential","Unrestricted","Unknown"]}/>
        <DistroCard title="CDE Tier Breakdown"
                    data={filteredAgg?.cde_tier ?? {}}
                    palette={CDE_COLORS}
                    fallbackColor={T.brand}
                    order={["Critical","Significant","Important","Standard","Unknown"]}/>
        <DistroCard title="Anonymization Risk"
                    data={filteredAgg?.risk_buckets ?? {}}
                    palette={{
                      "Very High (3+)": { color: "#991b1b", bg: "#fef2f2", border: "#fca5a5" },
                      "High (2–3)":     { color: "#9a3412", bg: "#fff7ed", border: "#fdba74" },
                      "Medium (1–2)":   { color: T.brand,  bg: T.brandLight, border: T.brandMid },
                      "Low (0–1)":      { color: T.green,  bg: T.greenBg,    border: T.greenBorder },
                    }}
                    fallbackColor={T.brand}
                    order={["Very High (3+)","High (2–3)","Medium (1–2)","Low (0–1)"]}/>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
        gap: 12, marginBottom: 18,
      }}>
        <DistroCard title="Regulatory Coverage"
                    data={filteredAgg?.regulatory ?? {}}
                    palette={{}} fallbackColor="#9a3412"
                    emptyMsg="No regulatory frameworks triggered for selected entities."/>
        <DistroCard title="Business Criticality"
                    data={filteredAgg?.criticality ?? {}}
                    palette={{
                      "High":    { color: "#991b1b", bg: "#fef2f2", border: "#fca5a5" },
                      "Medium":  { color: "#9a3412", bg: "#fff7ed", border: "#fdba74" },
                      "Low":     { color: T.green,  bg: T.greenBg,  border: T.greenBorder },
                      "Unknown": { color: T.textMuted, bg: T.bg, border: T.border },
                    }}
                    fallbackColor={T.textMuted}
                    order={["High","Medium","Low","Unknown"]}/>
      </div>

      {/* ── Entity selector ── */}
      <div style={{
        background: T.bgCard, border: `1px solid ${T.border}`,
        borderRadius: 12, overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      }}>
        <div style={{
          padding: "11px 18px", borderBottom: `1px solid ${T.border}`,
          background: T.bgSidebar, display: "flex", alignItems: "center",
          justifyContent: "space-between",
        }}>
          <div style={{
            fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.08em",
            textTransform: "uppercase", color: T.textLabel,
          }}>
            Entities in this source · click rows to include/exclude
          </div>
          <div style={{ fontSize: "0.7rem", color: T.textMuted }}>
            {selectedCount}/{totalEntities} selected
          </div>
        </div>

        <div style={{ maxHeight: 360, overflowY: "auto" }}>
          {entitiesInSource.map((e, idx) => {
            const s        = summaryByEntity.get(e.entityId);
            const isPicked = selectedIds.has(e.entityId);
            const total    = s?.field_count ?? 0;
            const approved = s?.approved_count ?? 0;
            const pct      = s?.approval_pct ?? 0;
            const hasData  = s?.has_process_data ?? false;
            return (
              <div
                key={e.entityId}
                onClick={() => toggleEntity(e.entityId)}
                style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: "11px 18px",
                  borderBottom: idx < entitiesInSource.length - 1
                    ? `1px solid ${T.border}` : "none",
                  background: isPicked ? "#fff" : "#fafbfd",
                  cursor: "pointer", transition: "background 0.1s",
                  opacity: hasData ? 1 : 0.78,
                }}
              >
                <div style={{
                  width: 16, height: 16, flexShrink: 0,
                  borderRadius: 4, border: `1.5px solid ${isPicked ? T.brand : T.border}`,
                  background: isPicked ? T.brand : "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.12s",
                }}>
                  {isPicked && (
                    <span style={{ color: "#fff", fontSize: 10, fontWeight: 800, lineHeight: 1 }}>✓</span>
                  )}
                </div>

                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    fontSize: "0.84rem", fontWeight: 600,
                    color: isPicked ? T.textPrimary : T.textSecond,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{e.entity}</div>
                  <div style={{ fontSize: "0.66rem", color: T.textMuted,
                    fontFamily: "monospace", marginTop: 2 }}>
                    {e.entityId}{e.domain ? ` · ${e.domain}` : ""}
                    {!hasData && (
                      <span style={{
                        marginLeft: 8, color: T.amber, fontWeight: 600,
                        fontStyle: "italic", fontFamily: "inherit",
                      }}>pipeline pending</span>
                    )}
                  </div>
                </div>

                {/* enrichment coverage — compact stacked bar */}
                {(() => {
                  const dm  = s?.dm_field_count ?? 0;
                  const bm  = s?.bm_enriched ?? 0;
                  const mc  = s?.mc_enriched ?? 0;
                  const cde = s?.cde_enriched ?? 0;
                  if (dm === 0 && !hasData) return (
                    <div style={{ width: 160, flexShrink: 0, fontSize: "0.62rem", color: T.textMuted, fontStyle: "italic", textAlign: "center" }}>
                      not enriched yet
                    </div>
                  );
                  const bmPct  = dm > 0 ? (bm / dm) * 100 : 0;
                  const mcPct  = dm > 0 ? (mc / dm) * 100 : 0;
                  const cdePct = dm > 0 ? (cde / dm) * 100 : 0;
                  return (
                    <div style={{ width: 160, flexShrink: 0 }}>
                      {/* Compact label row */}
                      <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        fontSize: "0.6rem", color: T.textMuted, marginBottom: 3,
                      }}>
                        <span style={{ fontWeight: 600 }}>{dm} fields</span>
                        <div style={{ display: "flex", gap: 6 }}>
                          {bm > 0 && <span style={{ color: T.brand, fontWeight: 700 }}>BM {bm}</span>}
                          {mc > 0 && <span style={{ color: "#6366f1", fontWeight: 700 }}>MC {mc}</span>}
                          {cde > 0 && <span style={{ color: "#9a3412", fontWeight: 700 }}>CDE {cde}</span>}
                          {bm === 0 && mc === 0 && cde === 0 && <span>0 enriched</span>}
                        </div>
                      </div>
                      {/* Stacked bar — 3 layers on same track */}
                      <div style={{ position: "relative", height: 6, background: T.border, borderRadius: 99, overflow: "hidden" }}>
                        {/* CDE (bottom layer — always visible if present) */}
                        <div style={{
                          position: "absolute", left: 0, top: 0, height: "100%",
                          width: `${cdePct}%`, background: "#fdba74",
                          borderRadius: 99, transition: "width 0.4s ease",
                        }}/>
                        {/* MC (middle layer) */}
                        <div style={{
                          position: "absolute", left: 0, top: 0, height: "100%",
                          width: `${mcPct}%`, background: "#a5b4fc",
                          borderRadius: 99, transition: "width 0.4s ease",
                          opacity: 0.7,
                        }}/>
                        {/* BM (top layer — usually the widest) */}
                        <div style={{
                          position: "absolute", left: 0, top: 0, height: "100%",
                          width: `${bmPct}%`, background: T.brand,
                          borderRadius: 99, transition: "width 0.4s ease",
                          opacity: 0.6,
                        }}/>
                        {/* Fully enriched overlay */}
                        {bm === dm && mc === dm && cde === dm && dm > 0 && (
                          <div style={{
                            position: "absolute", left: 0, top: 0, height: "100%", width: "100%",
                            background: T.green, borderRadius: 99,
                          }}/>
                        )}
                      </div>
                      {/* Legend dots — only when there's data */}
                      {(bm > 0 || mc > 0 || cde > 0) && (
                        <div style={{
                          display: "flex", gap: 8, marginTop: 3, fontSize: "0.52rem", color: T.textMuted,
                        }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <span style={{ width: 5, height: 5, borderRadius: "50%", background: T.brand, display: "inline-block" }}/> BM
                          </span>
                          <span style={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#a5b4fc", display: "inline-block" }}/> MC
                          </span>
                          <span style={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#fdba74", display: "inline-block" }}/> CDE
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* approval progress */}
                <div style={{ width: 120, flexShrink: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between",
                    fontSize: "0.66rem", color: T.textMuted, marginBottom: 3 }}>
                    <span>{approved}/{total} approved</span>
                    <span style={{
                      fontWeight: 700,
                      color: pct === 100 ? T.green : pct > 0 ? T.brand : T.textMuted,
                    }}>{pct}%</span>
                  </div>
                  <div style={{ height: 4, background: T.border,
                    borderRadius: 99, overflow: "hidden" }}>
                    <div style={{
                      width: `${pct}%`, height: "100%",
                      background: pct === 100 ? T.green : T.brand,
                      borderRadius: 99, transition: "width 0.4s ease",
                    }}/>
                  </div>
                </div>

                {/* tiny badges */}
                <div style={{ display: "flex", gap: 4, flexShrink: 0, minWidth: 110, justifyContent: "flex-end" }}>
                  {s && s.cde_count > 0 && (
                    <span style={{
                      fontSize: "0.6rem", fontWeight: 700,
                      padding: "2px 7px", borderRadius: 4,
                      background: "#fff7ed", border: "1px solid #fdba74", color: "#9a3412",
                    }}>{s.cde_count} CDE</span>
                  )}
                  {s && s.personal_data > 0 && (
                    <span style={{
                      fontSize: "0.6rem", fontWeight: 700,
                      padding: "2px 7px", borderRadius: 4,
                      background: T.brandLight, border: `1px solid ${T.brandMid}`, color: T.brand,
                    }}>PII</span>
                  )}
                </div>
              </div>
            );
          })}
          {entitiesInSource.length === 0 && (
            <div style={{
              padding: "32px 18px", textAlign: "center",
              color: T.textMuted, fontSize: "0.82rem", fontStyle: "italic",
            }}>
              No entities configured for this source.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Reusable bits ────────────────────────────────────────────────────────────
function pillBtn(color: string, muted: boolean): React.CSSProperties {
  return {
    padding: "6px 14px", borderRadius: 7,
    border: `1px solid ${muted ? T.border : color + "55"}`,
    background: "transparent", color: muted ? T.textMuted : color,
    fontSize: "0.72rem", fontWeight: 600, cursor: "pointer",
    transition: "all 0.12s",
  };
}

function Kpi({ label, value, sub, color, bg, border }: {
  label: string; value: string | number; sub?: string;
  color: string; bg: string; border: string;
}) {
  return (
    <div style={{
      padding: "14px 16px", borderRadius: 10,
      background: bg, border: `1px solid ${border}`,
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        fontSize: "0.6rem", fontWeight: 700,
        color, textTransform: "uppercase",
        letterSpacing: "0.08em", marginBottom: 6,
      }}>{label}</div>
      <div style={{
        fontSize: "1.55rem", fontWeight: 800,
        color, lineHeight: 1, marginBottom: 4,
      }}>{value}</div>
      {sub && (
        <div style={{ fontSize: "0.67rem", color, opacity: 0.7 }}>{sub}</div>
      )}
    </div>
  );
}

function DistroCard({
  title, data, palette, fallbackColor, order, emptyMsg,
}: {
  title:         string;
  data:          Record<string, number>;
  palette:       Record<string, { color: string; bg: string; border: string }>;
  fallbackColor: string;
  order?:        string[];
  emptyMsg?:     string;
}) {
  const entries = useMemo(() => {
    const e = Object.entries(data).filter(([, v]) => v > 0);
    if (order && order.length > 0) {
      e.sort((a, b) => {
        const ai = order.indexOf(a[0]);
        const bi = order.indexOf(b[0]);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });
    } else {
      e.sort((a, b) => b[1] - a[1]);
    }
    return e;
  }, [data, order]);

  const max = entries.reduce((m, [, v]) => Math.max(m, v), 0) || 1;

  return (
    <div style={{
      background: T.bgCard, border: `1px solid ${T.border}`,
      borderRadius: 10, overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        padding: "10px 16px", background: T.bgSidebar,
        borderBottom: `1px solid ${T.border}`,
        fontSize: "0.68rem", fontWeight: 700, color: T.textLabel,
        textTransform: "uppercase", letterSpacing: "0.08em",
      }}>{title}</div>
      <div style={{ padding: "12px 16px" }}>
        {entries.length === 0 ? (
          <div style={{
            fontSize: "0.75rem", color: T.textMuted,
            fontStyle: "italic", padding: "8px 0",
          }}>{emptyMsg ?? "No data"}</div>
        ) : entries.map(([k, v]) => {
          const c   = palette[k] ?? {
            color: fallbackColor, bg: T.brandLight, border: T.brandMid,
          };
          const pct = Math.round((v / max) * 100);
          return (
            <div key={k} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "5px 0",
            }}>
              <div style={{
                width: 126, flexShrink: 0, fontSize: "0.72rem",
                color: T.textSecond, overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap",
                textAlign: "right", fontWeight: 500,
              }}>{k}</div>
              <div style={{
                flex: 1, height: 15, background: T.bg,
                borderRadius: 4, overflow: "hidden",
                border: `1px solid ${T.border}`,
              }}>
                <div style={{
                  width: `${pct}%`, height: "100%",
                  background: c.bg,
                  borderRight: `2px solid ${c.color}`,
                  transition: "width 0.5s ease",
                }}/>
              </div>
              <span style={{
                fontSize: "0.72rem", fontWeight: 700,
                color: c.color, minWidth: 22, textAlign: "right",
              }}>{v}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LoadingShell() {
  return (
    <div style={{
      padding: 32, display: "flex", alignItems: "center",
      justifyContent: "center", color: T.textMuted, fontSize: "0.85rem",
    }}>
      <span style={{ marginRight: 10 }}>⟳</span> Loading source summary…
    </div>
  );
}

function ErrorShell({ msg }: { msg: string }) {
  return (
    <div style={{
      margin: 24, padding: "14px 18px",
      background: T.redBg, border: `1px solid ${T.redBorder}`,
      borderRadius: 8, color: T.red, fontSize: "0.84rem",
    }}>
      Failed to load source summary: {msg}
    </div>
  );
}

// ─── Aggregation helper (client-side filter without re-fetch) ─────────────────
function reduceEntities(list: DGEntitySummary[]) {
  const initBuckets = { "Low (0–1)": 0, "Medium (1–2)": 0,
                        "High (2–3)": 0, "Very High (3+)": 0 };
  const acc = {
    entity_count:   list.length,
    total_fields:   0,
    approved_count: 0,
    approval_pct:   0,
    personal_data:  0,
    anon_required:  0,
    cde_count:      0,
    critical_cde:   0,
    sensitivity:    {} as Record<string, number>,
    cde_tier:       {} as Record<string, number>,
    criticality:    {} as Record<string, number>,
    regulatory:     {} as Record<string, number>,
    risk_buckets:   { ...initBuckets } as Record<string, number>,
    dm_field_count: 0,
    bm_enriched:    0,
    mc_enriched:    0,
    cde_enriched:   0,
  };
  const merge = (a: Record<string, number>, b: Record<string, number>) => {
    for (const [k, v] of Object.entries(b)) a[k] = (a[k] ?? 0) + (v ?? 0);
  };
  list.forEach(e => {
    acc.total_fields   += e.field_count;
    acc.approved_count += e.approved_count;
    acc.personal_data  += e.personal_data;
    acc.anon_required  += e.anon_required;
    acc.cde_count      += e.cde_count;
    acc.critical_cde   += e.critical_cde;
    merge(acc.sensitivity, e.sensitivity);
    merge(acc.cde_tier,    e.cde_tier);
    merge(acc.criticality, e.criticality);
    merge(acc.regulatory,  e.regulatory);
    merge(acc.risk_buckets, e.risk_buckets);
    acc.dm_field_count += e.dm_field_count ?? 0;
    acc.bm_enriched    += e.bm_enriched ?? 0;
    acc.mc_enriched    += e.mc_enriched ?? 0;
    acc.cde_enriched   += e.cde_enriched ?? 0;
  });
  acc.approval_pct = acc.total_fields
    ? Math.round((acc.approved_count / acc.total_fields) * 100)
    : 0;
  return acc;
}